import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import PageHeader from '@features/PageHeader';
import SplitButton from '@components/SplitButton';
import { ModalConfirm } from '@components/Modal';
import { Typography } from '@components/Typography';
import EmailTemplateBuilder from '@features/EmailTemplateBuilder';
import WhatsAppTemplateBuilder from '@features/WhatsAppTemplateBuilder';
import { TemplateLibraryIcon } from 'assets/images/icons';
import { usePermissions, useTranslation } from '@hooks';
import { alertActions, brandsActions, campaignTemplatesActions, channelsActions } from '@actions';
import { campaignTemplatesService } from '@services';
import { handleApiError } from '@helpers/api';
import { CHANNEL } from '@constants/channels';
import { TEMPLATE_CHANNEL_FILTER, EMPTY_TEMPLATE_FILTER } from '@constants/campaignTemplates';
import TemplateCard from './TemplateCard';
import FiltersPanel from './FiltersPanel';

const CardsGrid = styled('div')({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
});

const isWhatsAppTemplate = template => template.channel === TEMPLATE_CHANNEL_FILTER.WHATSAPP;

const Library = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { hasFullAccess } = usePermissions();

    const { data: templates, loading } = useSelector(state => state.campaignTemplates);
    const channels = useSelector(state => state.channels.data);

    const [filter, setFilter] = useState(EMPTY_TEMPLATE_FILTER);
    const [builder, setBuilder] = useState({ open: false, channel: null, template: null, isView: false });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState({ el: null, id: null });

    const canManage = hasFullAccess('campaigns');

    useEffect(() => {
        dispatch(campaignTemplatesActions.get());
        dispatch(channelsActions.get('campaigns'));
        dispatch(brandsActions.get());
        campaignTemplatesService
            .syncWhatsAppTemplates()
            .then(() => dispatch(campaignTemplatesActions.get()))
            .catch(() => {});
    }, [dispatch]);

    const hasWhatsAppChannel = useMemo(
        () => (channels || []).some(channel => channel.type === CHANNEL.WHATSAPP_BUSINESS),
        [channels],
    );

    const availableChannelTypes = useMemo(
        () => [...new Set((templates || []).map(template => template.channel))],
        [templates],
    );

    const visibleTemplates = useMemo(
        () =>
            (templates || []).filter(
                template =>
                    (filter.channelTypes.length === 0 || filter.channelTypes.includes(template.channel)) &&
                    (filter.brandIds.length === 0 || filter.brandIds.includes(template.brandId)),
            ),
        [templates, filter],
    );

    const onOpenTemplate = template => {
        setBuilder({
            open: true,
            channel: template.channel,
            template,
            isView: !canManage,
        });
    };

    const onDuplicate = async template => {
        setMenuAnchor({ el: null, id: null });
        try {
            const copy = isWhatsAppTemplate(template)
                ? await campaignTemplatesService.duplicateWhatsAppTemplate(template.id)
                : await campaignTemplatesService.duplicateEmailTemplate(template.id);
            dispatch(campaignTemplatesActions.get());
            dispatch(alertActions.success(t('TemplateLibrary.duplicated')));
            setBuilder({ open: true, channel: template.channel, template: copy, isView: false });
        } catch (error) {
            handleApiError(error, 'alertMessages.editFail');
        }
    };

    const onArchive = async template => {
        setMenuAnchor({ el: null, id: null });
        try {
            if (isWhatsAppTemplate(template)) {
                if (template.isArchived) {
                    await campaignTemplatesService.unarchiveWhatsAppTemplate(template.id);
                } else {
                    await campaignTemplatesService.archiveWhatsAppTemplate(template.id);
                }
            } else if (template.isArchived) {
                await campaignTemplatesService.unarchiveEmailTemplate(template.id);
            } else {
                await campaignTemplatesService.archiveEmailTemplate(template.id);
            }
            dispatch(campaignTemplatesActions.get());
        } catch (error) {
            handleApiError(error, 'alertMessages.editFail');
        }
    };

    const onDelete = async () => {
        try {
            if (isWhatsAppTemplate(deleteTarget)) {
                await campaignTemplatesService.deleteWhatsAppTemplate(deleteTarget.id);
            } else {
                await campaignTemplatesService.deleteEmailTemplate(deleteTarget.id);
            }
            dispatch(campaignTemplatesActions.get());
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
        } catch (error) {
            handleApiError(error, 'TemplateLibrary.deleteBlocked');
        }
        setDeleteTarget(null);
    };

    return (
        <>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<TemplateLibraryIcon />}
                title={t('TemplateLibrary.title')}
                subtitle={t('TemplateLibrary.subtitle')}
            >
                {canManage && (
                    <SplitButton
                        variant="contained"
                        options={[
                            {
                                text: t('TemplateLibrary.newEmailTemplate'),
                                onClick: () =>
                                    setBuilder({
                                        open: true,
                                        channel: TEMPLATE_CHANNEL_FILTER.EMAIL,
                                        template: null,
                                        isView: false,
                                    }),
                            },
                            {
                                text: t('TemplateLibrary.newWhatsAppTemplate'),
                                disabled: !hasWhatsAppChannel,
                                tooltip: hasWhatsAppChannel ? null : t('TemplateLibrary.waChannelRequired'),
                                onClick: () =>
                                    setBuilder({
                                        open: true,
                                        channel: TEMPLATE_CHANNEL_FILTER.WHATSAPP,
                                        template: null,
                                        isView: false,
                                    }),
                            },
                        ]}
                    />
                )}
                <FiltersPanel filter={filter} setFilter={setFilter} channelTypes={availableChannelTypes} />
            </PageHeader>
            {!loading && visibleTemplates.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', marginTop: '24px' }}>
                    {t('TemplateLibrary.emptyState')}
                </Typography>
            ) : (
                <CardsGrid>
                    {visibleTemplates.map(template => (
                        <TemplateCard
                            key={template.id}
                            template={template}
                            canManage={canManage}
                            onOpen={onOpenTemplate}
                            onDuplicate={onDuplicate}
                            onArchive={onArchive}
                            onDelete={target => {
                                setMenuAnchor({ el: null, id: null });
                                setDeleteTarget(target);
                            }}
                            menuAnchor={menuAnchor}
                            onOpenMenu={(e, id) => setMenuAnchor({ el: e.currentTarget, id })}
                            onCloseMenu={() => setMenuAnchor({ el: null, id: null })}
                        />
                    ))}
                </CardsGrid>
            )}
            {builder.open && builder.channel === TEMPLATE_CHANNEL_FILTER.WHATSAPP && (
                <WhatsAppTemplateBuilder
                    isOpen={builder.open}
                    onClose={() => setBuilder({ open: false, channel: null, template: null, isView: false })}
                    template={builder.template}
                    isView={builder.isView}
                />
            )}
            {builder.open && builder.channel !== TEMPLATE_CHANNEL_FILTER.WHATSAPP && (
                <EmailTemplateBuilder
                    isOpen={builder.open}
                    onClose={() => setBuilder({ open: false, channel: null, template: null, isView: false })}
                    template={builder.template}
                    isView={builder.isView}
                />
            )}
            {deleteTarget && (
                <ModalConfirm
                    isOpen={!!deleteTarget}
                    handleClose={() => setDeleteTarget(null)}
                    title={t('common.deleteTitle')}
                    description={t('TemplateLibrary.deleteDescription', { name: deleteTarget.name })}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </>
    );
};

export default Library;
